import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { tokens } from "../../styles/tokens";

// Lazy-load the Lottie renderer (~656KB) — it's only needed after the
// deferred mount timer fires, so the main bundle never includes it.
const LazyDotLottieReact = lazy(() =>
  import("@lottiefiles/dotlottie-react").then((m) => ({ default: m.DotLottieReact }))
);

interface AIBotIconProps {
  size?: number;
}

/**
 * Lottie-based AI bot icon with deferred initialization.
 *
 * The DotLottieReact component initializes a WASM renderer on mount, which
 * involves deep call chains for decompression and canvas setup. On iOS/WebKit
 * (smaller ~16K call stack), this can cause "RangeError: Maximum call stack
 * size exceeded" when the component mounts during navigation transitions
 * (e.g., returning to /dashboard after the getting-started tour).
 *
 * Fix: defer the Lottie mount by a short timeout so it doesn't compete with
 * React's synchronous reconciliation during route changes. Show a lightweight
 * SVG placeholder until the animation is ready.
 */
const AIBotIcon: React.FC<AIBotIconProps> = React.memo(({ size = 24 }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    // Defer the Lottie WASM init so it doesn't overlap with React's
    // synchronous commit phase during route transitions.
    const rafId = requestAnimationFrame(() => {
      timer = setTimeout(() => setReady(true), 80);
    });
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
      setReady(false);
    };
  }, []);

  const style = useMemo(() => ({ width: size, height: size }), [size]);

  if (!ready) {
    // Lightweight placeholder: a simple circle matching the Lottie's
    // dominant colour so the FAB doesn't flash empty.
    return (
      <div style={style} aria-hidden>
        <svg viewBox="0 0 64 64" width={size} height={size}>
          <circle cx="32" cy="32" r="28" fill={tokens.indigo600} opacity="0.15" />
          <circle cx="32" cy="32" r="18" fill={tokens.indigo600} opacity="0.25" />
        </svg>
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div style={style} aria-hidden>
        <svg viewBox="0 0 64 64" width={size} height={size}>
          <circle cx="32" cy="32" r="28" fill={tokens.indigo600} opacity="0.15" />
          <circle cx="32" cy="32" r="18" fill={tokens.indigo600} opacity="0.25" />
        </svg>
      </div>
    }>
      <LazyDotLottieReact
        src="/ai_2.lottie"
        autoplay
        loop
        style={style}
      />
    </Suspense>
  );
});

AIBotIcon.displayName = "AIBotIcon";

export default AIBotIcon;
